// src/redux/slices/webinarAdminSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { webinarsService } from '../../services/webinarsService.js'
import { handleApiError } from '../../utils/handleApiError.js'

export const fetchWebinarsAdmin = createAsyncThunk(
    'webinarAdmin/fetchWebinarsAdmin',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await webinarsService.getWebinars({
                ...params,
                limit: params.limit || 10,
                page: params.page || 1
            })
            return {
                webinars: response.data.webinars,
                pagination: response.data.pagination
            }
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const createWebinar = createAsyncThunk(
    'webinarAdmin/createWebinar',
    async (webinarData, { rejectWithValue }) => {
        try {
            const response = await webinarsService.createWebinar(webinarData)
            return response.data.webinar
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const updateWebinar = createAsyncThunk(
    'webinarAdmin/updateWebinar',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await webinarsService.updateWebinar(id, data)
            return response.data.webinar
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const deleteWebinar = createAsyncThunk(
    'webinarAdmin/deleteWebinar',
    async (id, { rejectWithValue }) => {
        try {
            await webinarsService.deleteWebinar(id)
            return id
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const fetchWebinarRegistrations = createAsyncThunk(
    'webinarAdmin/fetchWebinarRegistrations',
    async ({ slug, params = {} }, { rejectWithValue }) => {
        try {
            const response = await webinarsService.getWebinarRegistrations(slug, params)
            return {
                slug,
                registrations: response.data.registrations,
                pagination: response.data.pagination
            }
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

const webinarAdminSlice = createSlice({
    name: 'webinarAdmin',
    initialState: {
        webinars: [],
        currentWebinar: null,
        registrations: [],
        loading: false,
        loadingRegistrations: false,
        pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false
        },
        registrationsPagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0
        },
        error: null
    },
    reducers: {
        clearCurrentWebinar: (state) => {
            state.currentWebinar = null
        },
        clearError: (state) => {
            state.error = null
        },
        clearRegistrations: (state) => {
            state.registrations = []
            state.registrationsPagination = {
                page: 1,
                limit: 10,
                total: 0,
                totalPages: 0
            }
        },
        setPagination: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Webinars Admin
            .addCase(fetchWebinarsAdmin.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchWebinarsAdmin.fulfilled, (state, action) => {
                state.loading = false
                state.webinars = action.payload.webinars
                state.pagination = action.payload.pagination
                state.error = null
            })
            .addCase(fetchWebinarsAdmin.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Create Webinar
            .addCase(createWebinar.pending, (state) => {
                state.loading = true
            })
            .addCase(createWebinar.fulfilled, (state, action) => {
                state.loading = false
                state.webinars.unshift(action.payload)
                state.error = null
            })
            .addCase(createWebinar.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Update Webinar
            .addCase(updateWebinar.pending, (state) => {
                state.loading = true
            })
            .addCase(updateWebinar.fulfilled, (state, action) => {
                state.loading = false
                const index = state.webinars.findIndex(webinar => webinar._id === action.payload._id)
                if (index !== -1) {
                    state.webinars[index] = action.payload
                }
                state.error = null
            })
            .addCase(updateWebinar.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            
            // Delete Webinar
            .addCase(deleteWebinar.fulfilled, (state, action) => {
                state.webinars = state.webinars.filter(webinar => webinar._id !== action.payload)
                state.error = null
            })
            
            // Fetch Webinar Registrations
            .addCase(fetchWebinarRegistrations.pending, (state) => {
                state.loadingRegistrations = true
            })
            .addCase(fetchWebinarRegistrations.fulfilled, (state, action) => {
                state.loadingRegistrations = false
                state.registrations = action.payload.registrations
                state.registrationsPagination = action.payload.pagination
                state.error = null
            })
            .addCase(fetchWebinarRegistrations.rejected, (state, action) => {
                state.loadingRegistrations = false
                state.error = action.payload
            })
    }
})

export const { clearCurrentWebinar, clearError, clearRegistrations, setPagination } = webinarAdminSlice.actions
export default webinarAdminSlice.reducer